import { ScheduleOutlined } from '@ant-design/icons';
import { Button, Card, Col, type ModalProps, Row, Tooltip } from 'antd';
import axios from 'axios';
import dayjs from 'dayjs';
import type React from 'react';
import { type FC, useState } from 'react';
import { ScheduleCardsSkeleton } from '@/components/skeletons/ScheduleCardsSkeleton';
import { AppModal } from '@/components/ui/AppModal';
import { logError } from '@/utils/formatError';

interface ScheduleResponse {
  [date: string]: [string, string][];
}

interface ScheduleModalProps {
  districtId: string;
  districtName: string;
}

export const ScheduleModal: FC<ScheduleModalProps> = ({ districtId, districtName }) => {
  const [schedule, setSchedule] = useState<ScheduleResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  const fetchSchedule = async () => {
    setLoading(true);
    try {
      const response = await axios.get<ScheduleResponse>(
        `https://api.zaslogistica.com/schedule/district_id/${districtId}`
      );
      setSchedule(response.data);
    } catch (error) {
      logError(error, 'Fetch District Schedule');
      console.error('Error fetching schedule:', error);
      setSchedule(null);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
    e.stopPropagation();
    setVisible(true);
    fetchSchedule();
  };

  const handleClose: NonNullable<ModalProps['onCancel']> = (e) => {
    e.stopPropagation();
    setVisible(false);
    setSchedule(null);
  };

  const renderSchedule = () => {
    if (!schedule) return null;

    return (
      <Row gutter={16}>
        {Object.entries(schedule).map(([date, times]) => (
          <Col key={date} span={3}>
            <Card title={`${date} (${dayjs(date).format('ddd')})`} variant="outlined">
              {times.map(([start, end]) => (
                <div key={`${start}-${end}`} style={{ marginBottom: '8px', textAlign: 'center' }}>
                  <Button type="dashed" block>
                    {start} - {end}
                  </Button>
                </div>
              ))}
            </Card>
          </Col>
        ))}
      </Row>
    );
  };

  return (
    <>
      <Tooltip title={'Schedule for district'}>
        <Button type="link" onClick={handleOpen} icon={<ScheduleOutlined />} />
      </Tooltip>

      <AppModal
        title={`Schedule for ${districtName}`}
        open={visible}
        onCancel={handleClose}
        footer={null}
        zIndex={2000}
        width={'auto'}
      >
        {loading ? (
          <ScheduleCardsSkeleton />
        ) : schedule ? (
          renderSchedule()
        ) : (
          <p>No schedule available for this district.</p>
        )}
      </AppModal>
    </>
  );
};
